import axios, { AxiosInstance } from 'axios';
export const apiHost = import.meta.env.VITE_API_HOST;
import type { StateName } from './constants';
import type { TherapyIndication, Sex, PhoneType } from './components/tasks/tremfyaExpressEnrollmentTask/dropdownValues';
import type { Drug as SharedUtilsDrug } from 'shared-utils/types/drug';
import { Role, Specialty } from './types';

export interface Drug extends SharedUtilsDrug {}

export const client: AxiosInstance = axios.create({
  baseURL: apiHost,
  withCredentials: true,
});

export const url = (path: string) => {
  return `${apiHost}${path}`;
};

export const getRxFileDownloadUrl = (rxId: string, fileId: string) => {
  return `${apiHost}/rxs/${rxId}/file/${fileId}/download`;
};

export const getRxFilePreviewUrl = (rxId: string, fileId: string) => {
  return `${apiHost}/rxs/${rxId}/file/${fileId}/preview`;
};

export const getRxFileFilenameUrl = (rxId: string, fileId: string) => {
  return `${apiHost}/rxs/${rxId}/file/${fileId}/filename`;
};

export interface ErrorResponse {
  error?: string;
  detail?: string;
}

// ------------------------------------------------------------
// Fax Intake - Processing Prescriptions
// ------------------------------------------------------------

// Keep in sync with python/models/fax_prescription_intake.py:FaxPrescriptionIntakeState
export type FaxPrescriptionIntakeState =
  | 'pending_triage'
  | 'verification_required'
  | 'pending_provider_contact'
  | 'awaiting_provider_response'
  | 'unable_to_contact_provider'
  | 'ready_for_entry'
  | 'ready_for_submission'
  | 'completed'
  | 'cannot_intake'
  | 'cancelled'
  | 'not_a_prescription'
  | 'duplicate';

export interface ProcessingPrescriptionResponseRow {
  id: string;
  state: FaxPrescriptionIntakeState;
  created_at: string;
  patient_name?: string | null;
  patient_dob?: string | null;
  provider_npi?: string | null;
  prescriber_name?: string | null;
  drug_name?: string | null;
}
export interface ProcessingFaxPrescriptionsResponse {
  processing_prescriptions: ProcessingPrescriptionResponseRow[];
}

// ------------------------------------------------------------
// Task Page
// Some of these have been split out to tasks/types.ts.
// New types should ideally be added to tasks/types.ts, and
// types under here can opportunistically be moved as long as we're
// not creating import cycles
// ------------------------------------------------------------

export enum PartnerTaskStatusFilter {
  OPEN = 'open',
  SENT = 'sent',
  COMPLETED = 'completed',
  ALL = 'all',
}

export type TaskStatus = 'open' | 'sent' | 'completed';

export interface TaskTableDrug {
  prescriber_name: string;
  prescription_recieved_at: string;
  drug_name: string;
  drug_category: 'biologic' | 'non_biologic' | 'provider_administered';
}

export interface TaskSummary {
  id: string;
  created_by: string;
  patient_name: string | null;
  patient_dob: string | null;
  last_comment_created_at: string;
  last_comment_message: string;
  last_comment_attachments_count: number;
  status: TaskStatus;
  // User-facing task type label
  task_type: TaskType;
  // Stable key for task type (backend enum value)
  task_key: TaskKey;
  drug: TaskTableDrug | null;
  office_name: string | null;
  task_title: string | null;
  // Denial prediction expiration metadata for dynamic countdown in the premium missing info task
  denial_pred_expiration_metadata?: DenialPredExpirationMetadata | null;
}

export interface NotificationRow {
  id: string;
  created_at: string;
  patient_name: string;
  patient_dob: string;
  drug_description: string;
  // User-facing task type label
  task_type: OutboundTaskType;
  status: string;
  rx_id: string;
  prescriber_name: string;
  office_name: string;
  extra_context: string;
}

export interface TasksResponse {
  tasks: TaskSummary[];
}

export interface NotificationsResponse {
  notifications: NotificationRow[];
}

export type TaskTimelineItemType = 'comment' | 'event';

export interface TaskComment {
  id: string;
  item_type: TaskTimelineItemType;
  direction: 'inbound' | 'outbound';
  message: string;
  user: string;
  attachment_document_ids: string[];
  created_at: string;
  last_edited_at?: string;
}

export interface TaskEvent {
  id: string;
  item_type: TaskTimelineItemType;
  event_timestamp: string;
  label: string;
  user?: string;
  action?: string;
  attachment_document_ids?: string[];
  event_metadata?: Record<string, unknown>;
}

/**
 * Old type based on frontend-facing labels.
 * TODO(ahu): remove in favor of task_key field.
 */
export type OutboundTaskType =
  | 'Additional clinical information'
  | 'Affordability issue'
  | 'Appeal drafted - Ready for review'
  | 'Cash pay denial'
  | 'Document upload'
  | 'Finalize EHR note'
  | 'New Rx document upload'
  | 'Form completion request'
  | 'Form submission'
  | 'PA resubmission opportunity'
  | 'Review Letter of Medical Necessity'
  | 'New Rx request'
  | 'Select cash price next steps'
  | 'Other'
  | 'PA approval'
  | 'PA denial'
  | 'PA denied - Select next step'
  | 'PA denied - Ready to appeal'
  | 'PA expiring'
  | 'PA on hold - Missing information'
  | 'PA on hold - Coverage criteria alert'
  | 'PA submitted - Plan requires additional information'
  | 'PA review'
  | 'PAP/Bridge'
  | 'Patient action required'
  | 'Preferred Pharmacy'
  | 'Prescription transfer notification'
  | 'Rx clarification'
  | 'Unable to verify insurance'
  | 'Unresponsive patient'
  | 'Onboarding - Send first prescription'
  | 'Onboarding - CMM fax verification'
  | 'Onboarding - EHR integration'
  | 'Missing fulfillment preference'
  | 'Confirm fulfillment option';

/**
 * Old type based on frontend-facing labels.
 * TODO(ahu): remove in favor of task_key field.
 */
export type InboundTaskType =
  | 'Authorization update'
  | 'PAP/Bridge update'
  | 'Pharmacy'
  | 'PA renewal'
  | 'Other'
  | 'Help request';

/**
 * Backend enum values for outbound tasks (python/models/task_queue.py:PartnerTaskType).
 *
 * If adding a new structured task, make sure to update rendering fork in TaskPage.tsx
 */
export enum OutboundTaskTypeKey {
  ADDITIONAL_CLINICAL_INFORMATION = 'additional_clinical_information',
  AFFORDABILITY = 'affordability',
  APPEAL_REVIEW = 'appeal_review',
  BENEFITS_ROUTING_CONFIRMATION = 'benefits_routing_confirmation',
  BENEFITS_ROUTING_SELECTION = 'benefits_routing_selection',
  BENEFITS_VERIFICATION_NEXT_STEPS = 'benefits_verification_next_steps',
  CASH_PAY_DENIAL = 'cash_pay_denial',
  DIGITAL_ENROLLMENT_TREMFYA = 'digital_enrollment_tremfya',
  DOCUMENT_UPLOAD = 'document_upload',
  DOCUMENT_UPLOAD_AWAITING_NOTES = 'document_upload_awaiting_notes',
  FINALIZE_EHR_NOTES = 'finalize_ehr_notes',
  FORM_SIGNATURE = 'form_signature',
  FORM_SUBMISSION = 'form_submission',
  FORMULARY_UPDATE = 'formulary_update',
  GIA_SUB_Q = 'gia_sub_q',
  ICD10_CODE_REQUEST = 'icd10_code_request',
  LETTER_OF_MEDICAL_NECESSITY = 'letter_of_medical_necessity',
  NEW_RX_DOCUMENT_UPLOAD = 'new_rx_document_upload',
  NEW_RX_REQUEST = 'new_rx_request',
  OFFERED_BEST_CASH_PRICE = 'offered_best_cash_price',
  ONBOARDING_EHR_INTEGRATION = 'onboarding_ehr_integration',
  ONBOARDING_FAX_VERIFICATION = 'onboarding_fax_verification',
  ONBOARDING_SEND_FIRST_RX = 'onboarding_send_first_rx',
  OTHER = 'other',
  PA_APPROVAL = 'pa_approval',
  PA_DENIAL = 'pa_denial',
  PA_DENIAL_NEXT_STEPS = 'pa_denial_next_steps',
  PA_DENIAL_NEXT_STEPS_AND_APPEAL_REVIEW = 'pa_denial_next_steps_and_appeal_review',
  PA_DENIAL_NEXT_STEPS_DYNAMIC = 'pa_denial_next_steps_dynamic',
  PA_EXPIRING = 'pa_expiring',
  PA_INFORMATION_REQUEST = 'pa_information_request',
  PA_INFORMATION_REQUEST_GIA_SUB_Q = 'pa_information_request_gia_sub_q',
  PA_INFORMATION_REQUEST_UNMET_CRITERIA = 'pa_information_request_unmet_criteria',
  PA_RESUBMISSION = 'pa_resubmission',
  PA_REVIEW = 'pa_review',
  PA_RFI_INFO_REQUEST = 'pa_rfi_info_request',
  PAP_BRIDGE = 'pap_bridge',
  PATIENT_ACTION_REQUIRED = 'patient_action_required',
  PHARMACY_REQUESTED = 'pharmacy_requested',
  PRESCRIPTION_TRANSFER_NOTIFICATION = 'prescription_transfer_notification',
  RX_CLARIFICATION = 'rx_clarification',
  UNABLE_TO_VERIFY_INSURANCE = 'unable_to_verify_insurance',
  UNRESPONSIVE_PATIENT = 'unresponsive_patient',
}

/**
 * Backend enum values for inbound tasks (python/models/task_queue.py:InboundTaskSubtype).
 */
export enum InboundTaskTypeKey {
  AUTHORIZATION_UPDATE = 'authorization_update',
  PAP_BRIDGE_UPDATE = 'pap_bridge_update',
  PHARMACY = 'pharmacy',
  PA_RENEWAL = 'pa_renewal',
  OTHER = 'other',
  HELP_REQUEST = 'help_request',
}

// TODO(ahu): remove in favor of TaskKey
export type TaskType = OutboundTaskType | InboundTaskType;
export type TaskKey = OutboundTaskTypeKey | InboundTaskTypeKey;

export type FlagContents = {
  title: string;
  description: string;
  link_url?: string;
  link_text?: string;
};

export type ExtendedInboundTaskOption = {
  label: string;
  value: string;
  blocking_flag?: FlagContents;
  info_flag?: FlagContents;
  auto_action_url?: string;
  // User-facing task type label
  task_type: InboundTaskType;
  option_bucket: OptionSection;
};

export type OptionSection =
  | 'Prescription status'
  | 'Insurance'
  | 'PA renewal'
  | 'PA'
  | 'Appeal'
  | 'Enrollments and manufacturer programs';

export type TaskTimelineItem = TaskComment | TaskEvent;

export enum TherapyTask {
  SavingsProgram = 'Savings Program',
  NurseGuide = 'Nurse Guide',
}

// keep in sync with python/program_enrollment/digital_enrollment_tremfya/schema.py
// this is the version of the form data that is fetched from and submitted to the backend
export type TremfyaExpressEnrollmentFormData = {
  // patient data
  patient_first_name: string;
  patient_last_name: string;
  patient_dob: string;
  patient_sex: Sex;
  patient_email: string | null;
  patient_phone: string;
  patient_phone_type: PhoneType;
  patient_address: string;
  patient_city: string;
  patient_state: StateName;
  patient_zip: string;
  // provider data
  provider_npi: string;
  provider_address: string;
  provider_practice_name: string;
  // program data for both types
  therapy_task: TherapyTask;
  therapy_indication: TherapyIndication;
  // nurse guide data
  include_nurse_guide: boolean | null;
  // savings program data
  patient_authorization: boolean | null;
  requirements_provided: boolean | null;
  is_eligible: boolean | null;
  has_existing_card: boolean | null;
};

// this is the version of the form data that is used in the actual form while it's being filled out
export type TremfyaExpressEnrollmentFormDataDefaultValues = {
  [key in keyof TremfyaExpressEnrollmentFormData]: TremfyaExpressEnrollmentFormData[key] | string;
} & {
  // override these fields as strings to render a radio group in the frontend form
  has_existing_card: 'Yes' | 'No' | null;
  is_eligible: 'Yes' | 'No' | null;
};

export interface Address {
  city: string;
  line1: string;
  line2: string;
  postal_code: string;
  state: string;
}
export interface Patient {
  id: string;
  address: Address;
  date_of_birth: string;
  first_name: string;
  last_name: string;
  phone_number: string;
}

export interface Prescriber {
  name: string;
  npi: string;
  phone_number: string;
  fax_number: string;
  address: Address;
}

export type TremfyaExpressEnrollmentStatus = 'draft' | 'completed' | 'not_eligible' | 'submitted' | 'error';

export interface TremfyaExpressEnrollmentResponse {
  form_data: TremfyaExpressEnrollmentFormData | null;
  drug: Drug;
  status: TremfyaExpressEnrollmentStatus;
  show_redraft_banner: boolean;
}

export const fileTypes = [
  'Medical Records',
  'Lab Results',
  'Insurance Card Both Sides',
  'Insurance Card Front',
  'Insurance Card Back',
  'PA Denial Letter',
  'PA Approval Letter',
  'Other',
] as const;

export const fileTypeToUILabel: Record<FileType, string> = {
  'Medical Records': 'Medical records',
  'Lab Results': 'Lab results',
  'Insurance Card Both Sides': 'Insurance card',
  'Insurance Card Front': 'Insurance card (front)',
  'Insurance Card Back': 'Insurance card (back)',
  'PA Denial Letter': 'PA denial letter',
  'PA Approval Letter': 'PA approval letter',
  Other: 'Other',
} as const;

export type FileType = (typeof fileTypes)[number];

export type NewCommentFile = {
  file: File;
  document_type: FileType;
};

interface FileInfoTag {
  name: string;
  value: FileType;
}

export type FormFilesDocumentTypes = {
  // The key will be a filename (like 'cat1.jpg')
  [key: string]: FileType;
};

export interface FileInfoResponse {
  id: string;
  filename: string;
  success: boolean;
  tags: FileInfoTag[];
}

/** Expiring Prior Auth Page */

export type PADecision = 'PENDING' | 'APPROVAL' | 'ALREADY_APPROVED' | 'DENIAL' | 'PARTIAL_APPROVAL' | 'NOT_REQUIRED';

export type PASubmissionMode = 'CMM' | 'FAX' | 'PHONE' | 'OTHER_TOOL' | 'CENTERX' | 'PROMPTPA';

export interface ExpiringPriorAuth {
  id: string;
  submitted_on_date: string;
  request_type: string;
  decision: PADecision;
  approval_until: string;
  submission_mode: PASubmissionMode;
  dismissed: boolean;
}

export interface ExpiringPriorAuthTableResponseRow {
  pa: ExpiringPriorAuth;
  rx: Drug;
  office_name: string;
  drug_name: string;
}

export interface ExpiringPriorAuthTableRow {
  formatted_prescription_recieved_at: string;
  formatted_approval_until: string;
  formatted_dob: string;
  office_name: string;
  drug_name: string;
  pa: ExpiringPriorAuth;
  rx: Drug;
}
export interface ExpiringPriorAuthResponse {
  patient: Patient;
  prescriber: Prescriber;
  drug: Drug;
  pa: ExpiringPriorAuth;
}

/** Prior Auth Review Page */

export type PriorAuthReviewStatus = 'unreviewed' | 'editing' | 'edited' | 'submitted';

export type PriorAuthReviewFormStatus =
  | 'FORM_STATUS_UNKNOWN'
  | 'FORM_STATUS_NOT_STARTED'
  | 'FORM_STATUS_IN_PROGRESS'
  | 'FORM_STATUS_COMPLETED'
  | 'FORM_STATUS_SUBMITTED';

// A row in the prior auth reviews table
export interface PriorAuthReviewSummary {
  id: string;
  drug_name: string;
  patient_name: string;
  patient_dob: string;
  prescriber_name: string;
  prescription_recieved_at: string;
  status: PriorAuthReviewStatus;
  created_at: string;
  office_name: string;
  signature_required: boolean;
}

// All rows in the prior auth reviews table
export interface PriorAuthReviewsResponse {
  prior_auth_reviews: PriorAuthReviewSummary[];
  practice_specialty: Specialty | null;
}

export interface PriorAuthReview {
  id: string;
  status: PriorAuthReviewStatus;
  reviewed_by?: string;
  reviewed_at?: string;
  submission_form_tree_state_id: string;
  attestation_submitted: boolean;
  signature_required: boolean;
  embed_document_url?: string;
  signature_completed: boolean;
}

// keep in sync with python/models/letter_of_medical_necessity.py
type LmnStatus = 'draft' | 'approved' | 'rejected' | 'submitted';
type LetterOfMedicalNecessity = {
  id: string;
  document_id: string;
  editable_letter_content: string;
  type: string;
  status: LmnStatus;
};

// Info in the PA review detail view
export interface PriorAuthReviewResponse {
  prior_auth_review: PriorAuthReview;
  letter_of_medical_necessity?: LetterOfMedicalNecessity;
  patient: Patient;
  prescriber: Prescriber;
  drug: Drug;
  icd_code: string;
}

type PriorAuthReviewFormAnswerType =
  | 'ANSWER_TYPE_UNKNOWN'
  | 'ANSWER_TYPE_CHECKBOX'
  | 'ANSWER_TYPE_DATE'
  | 'ANSWER_TYPE_RADIO'
  | 'ANSWER_TYPE_TEXT'
  | 'ANSWER_TYPE_JUSTIFICATION';

type PriorAuthReviewFormQuestion = {
  identifier: string;
  description: string;
  answer_type: PriorAuthReviewFormAnswerType;
  is_answered?: boolean;
};

export type PriorAuthReviewFormAnswer = {
  identifier: string;
  description?: string;
  answer_type: PriorAuthReviewFormAnswerType;
  regex_validations?: string;
  is_selected?: boolean; // if the question is answered + this answer is selected = True
  answer_input_text?: string; // populated for TEXT + DATE types
  answer_choice_text?: string; // this was not in the Retool code
};

export type PriorAuthReviewFormQuestionAnswer = {
  question: PriorAuthReviewFormQuestion;
  answer_options: PriorAuthReviewFormAnswer[];
};

export interface PriorAuthReviewFormData {
  form: {
    ordered_question_answers: PriorAuthReviewFormQuestionAnswer[];
  };
  form_status: PriorAuthReviewFormStatus;
}

type PriorAuthReviewFormAnswerSelection = {
  answer_identifier: string;
  // Passed in if answer_type is text or date
  answer_text?: string;
};

export type PriorAuthReviewFormUpdateData = {
  question_identifier: string;
  // If selected_answers is undefined, skip_question should be true
  selected_answers?: PriorAuthReviewFormAnswerSelection[];
  skip_question?: boolean;
};

export interface AcceptPriorAuthReviewData {
  attestation_submitted: boolean;
  letter_of_medical_necessity_id?: string;
  letter_of_medical_necessity_status?: LmnStatus;
}

/** Rx Page */
export type RxTaskType = 'task' | 'prior_auth_review';

export type RxTaskSubtype = TaskType | 'Appeal drafted - Ready for review' | 'PA review';

export type RxTaskListType = 'all' | 'tasks' | 'reviews' | 'none';

export interface RxTask {
  id: string;
  status: 'open' | 'closed';
  type: RxTaskType;
  sub_type: RxTaskSubtype;
  created_at: string;
  extra_context?: string;
}

export interface RxDocument {
  id: string;
  name: string;
  date: string;
  document_type: FileType;
}

export interface LastContactedPatient {
  action: 'texted' | 'called' | null;
  timestamp: string | null;
}

export enum TimelineEventAttachmentType {
  DOCUMENT = 'document',
  TASK = 'task',
  PA_REVIEW = 'pa_review',
  PA_SUBMISSION = 'pa_submission',
}

export enum TimelineEventAttachmentStatus {
  OPEN = 'open',
  CLOSED = 'closed',
}

export interface TimelineEventBasic {
  event: string;
  timestamp: string | null;
  is_status_change: boolean;
  subtitle?: string | null;
}

export interface TimelineEventWithDocument extends TimelineEventBasic {
  attachment_type: TimelineEventAttachmentType.DOCUMENT;
  attachment_data: {
    document_id: string;
    document_type: string;
  };
}

export interface TimelineEventWithTask extends TimelineEventBasic {
  attachment_type: TimelineEventAttachmentType.TASK;
  attachment_data: {
    task_id: string;
    status: TimelineEventAttachmentStatus;
    created_at?: string;
  };
}

export interface TimelineEventWithPAReview extends TimelineEventBasic {
  attachment_type: TimelineEventAttachmentType.PA_REVIEW;
  attachment_data: {
    review_id: string;
    status: TimelineEventAttachmentStatus;
    created_at?: string;
  };
}

export interface TimelineEventWithPASubmission extends TimelineEventBasic {
  attachment_type: TimelineEventAttachmentType.PA_SUBMISSION;
  attachment_data: {
    form_tree_state_id: string | null;
    submission_merged_document_id: string | null;
  };
}

export type TimelineEvent =
  | TimelineEventBasic
  | TimelineEventWithDocument
  | TimelineEventWithTask
  | TimelineEventWithPAReview
  | TimelineEventWithPASubmission;

export type RxTimelineStatus = 'in_progress' | 'completed_success' | 'completed_non_success' | 'awaiting_office';

export interface StatusTimeline {
  status: RxTimelineStatus;
  status_detail: string;
  timeline: TimelineEvent[];
}

export interface Rx {
  name: string;
  task_list_type: RxTaskListType;
  status_timelines: {
    coverage: StatusTimeline;
    pharmacy: StatusTimeline;
    bridge?: StatusTimeline;
    pap?: StatusTimeline;
    hub?: StatusTimeline;
  };
  pa_decision: PADecision;
  tasks: RxTask[];
  notifications: NotificationRow[];
  documents: RxDocument[];
  last_contacted_patient: LastContactedPatient;
  is_canceled: boolean;
  is_completed: boolean;
}

export interface Pharmacy {
  name: string;
  npi: string;
  phone: string;
  fax: string;
  address: Address;
}

export interface Insurance {
  name?: string;
  phone?: string;
  fax?: string;
  member_id?: string;
  bin?: string;
  pcn?: string;
  group_id?: string;
  form_name?: string;
}

export interface CopayData {
  amount: number;
  raw_text: string | null;
}

export interface CoinsuranceData {
  percentage: number;
  basis: string;
  raw_text: string | null;
}

export interface Accumulator {
  limit: number;
  used: number;
  remaining: number;
}

export interface CostShare {
  copay?: CopayData;
  coinsurance?: CoinsuranceData;
  individual_deductible?: Accumulator;
  individual_out_of_pocket_max?: Accumulator;
}

export interface BenefitsVerificationDetails {
  verification_id: string;
  payer_id: string;
  created_at: string;
  has_mb_coverage: boolean | null;
  eligibility_termination_date: string | null;
  has_other_coverage: boolean | null;
  supplementary_plan_type: string | null;
  cob_status: string | null;
  administration_network_status: string | null;
  number_of_visits: number | null;
  drug_cost_share: CostShare | null;
  admin_cost_share: CostShare | null;
  call_reference_id: string | null;
  notes: string | null;
  verified_by: string | null;
  verified_at: string | null;
}

export interface BenefitsPreferenceDetails {
  id: string;
  benefits_preference: string;
  care_site: string;
  care_site_address: string;
  hcpcs: string;
  cpt: string | null;
  rendering_provider_npi: string | null;
  tin: string | null;
}

export interface BenefitsVerificationResponse {
  verifications: BenefitsVerificationDetails[];
  preference: BenefitsPreferenceDetails | null;
}

export interface RxResponse {
  rx: Rx;
  patient: Patient;
  prescriber: Prescriber;
  drug: Drug;
  insurance: Insurance;
  pharmacy: Pharmacy;
  prescription_recieved_at: string;
  patient_dob: string;
  drug_name: string;
  prescriber_name: string;
  patient_name: string;
  show_update_pharmacy_button: boolean;
  benefits_verification: BenefitsVerificationResponse | null;
}

export type StatusInfo = {
  status: string;
  file_id: string | null;
};

export interface PharmacyAddress extends Address {
  name: string;
}

export interface RxTableResponseRow extends Drug {
  prescriber_name: string;
  patient_name: string;
  patient_dob: string;
  pharmacy_address: PharmacyAddress;
  name: string;
  drug_name: string;
  pa_appeal_status: StatusInfo;
  pap_status: StatusInfo;
  bridge_status: StatusInfo;
  hub_status: StatusInfo;
  pharmacy_status: StatusInfo;
  portal_status: 'open' | 'closed';
}

/** Unified search result that can represent either a Prescription or a Processing Prescription */
export interface UnifiedRxSearchResult {
  id: string;
  source: 'prescription' | 'processing';
  patient_name: string | null;
  patient_dob: string | null;
  drug_name: string | null;
  drug_description: string | null;
  prescriber_name: string | null;
  created_at: string;
  portal_status: 'open' | 'closed' | 'pending';
}

export interface RxSearchResponse {
  results: UnifiedRxSearchResult[];
}

/** Settings types */
export type NotificationSettings = {
  email_address?: string;
  selected_npis_email?: string[] | null;
  selected_npis_filters?: string[] | null;
};

export type OrgMemberRole = 'stytch_member' | 'stytch_admin';

export type OrgMember = {
  id: string;
  email_address: string;
  trusted_metadata: NotificationSettings;
  is_admin: boolean;
  name?: string;
  is_current_user: boolean;
  last_login?: string;
  job_title?: Role;
  contact_phone?: string;
};

export type OrgProvider = {
  npi: string;
  name: string | null;
  first_name: string | null;
  last_name: string | null;
};

export type OrgData = {
  members: OrgMember[];
  providers: OrgProvider[];
  show_users_tab: boolean;
};

export type NotificationData = {
  member: {
    email_address: string;
    selected_npis_email?: string[] | null;
    selected_npis_filters?: string[] | null;
    name: string;
  };
  providers: OrgProvider[];
};

export enum DrugCategory {
  Biologic = 'biologic',
  NonBiologic = 'non_biologic',
  CGRP = 'cgrp',
  ComplexTopical = 'complex_topical',
  ProviderAdministered = 'provider_administered',
}

export enum OverallStatusFilter {
  InProgress = 'in_progress',
  Completed = 'completed',
  All = 'all',
}

export enum InsuranceOption {
  UseExistingInsurance = 'use_existing_insurance',
  NewInsurance = 'new_insurance',
  NoInsurance = 'no_insurance',
  InsuranceUnavailable = 'insurance_unavailable',
}

/** Onboarding types */

export type EHRIntegrationOnboardingStatus = 'awaiting_credentials' | 'credentials_verified' | 'credentials_failed';
export type FaxVerificationOnboardingStatus =
  | 'awaiting_account_setup'
  | 'awaiting_prescriber_verification'
  | 'awaiting_fax_trigger'
  | 'awaiting_tandem_verification'
  | 'verification_successful'
  | 'verification_failed';
export type TrainingSessionOnboardingStatus = 'awaiting_booking' | 'booked' | 'declined';

export type PracticeOnboardingStatuses = {
  ehr_integration_status: EHRIntegrationOnboardingStatus;
  fax_verification_status: FaxVerificationOnboardingStatus;
  training_session_status: TrainingSessionOnboardingStatus;
  has_invited_teammates: boolean;
  has_added_providers: boolean;
  has_added_phone_numbers: boolean;
  has_ehr_integration: boolean;
  has_added_office: boolean;
  has_queued_prescriptions: boolean;
  has_saved_notification_preferences: boolean;
};

export type OnboardingInfoResponse = {
  practice_statuses: PracticeOnboardingStatuses;
  num_prescriptions_blocked: number;
};

/** Denial Prediction Task */
export type DenialPredExpirationMetadata = {
  expired_at: string;
  is_held: boolean;
};

/** Benefit Preferences */

export type SiteOfCareType = 'office' | 'hospital' | 'infusion_center';

export const SITE_OF_CARE_TYPE_LABELS: Record<SiteOfCareType, string> = {
  office: 'Office',
  hospital: 'Hospital',
  infusion_center: 'Infusion Center',
};

export type SiteOfCare = {
  id: string;
  office_address: string;
  tin: string;
  site_type: SiteOfCareType;
  address_line_1: string;
  address_line_2: string | null;
  city: string;
  state: string;
  zipcode: string;
};

export type SitesOfCareResponse = {
  sites: SiteOfCare[];
};

export type SiteOfCareRequest = {
  office_address: string;
  tin: string;
  site_type: SiteOfCareType;
  address_line_1: string;
  address_line_2: string | null;
  city: string;
  state: string;
  zipcode: string;
};

export type BenefitPreferenceType = 'buy_and_bill' | 'white_bagging' | 'prompt_everytime';

export const BENEFIT_PREFERENCE_TYPE_LABELS: Record<BenefitPreferenceType, string> = {
  buy_and_bill: 'Buy-and-bill',
  white_bagging: 'Specialty pharmacy (white bagging)',
  prompt_everytime: 'Ask for each Rx',
};

export type BenefitPreference = {
  id: string;
  drug_name: string;
  drug_form: string | null;
  drug_ndc: string | null;
  hcpcs: string;
  cpt: string;
  site_of_care_id: string | null;
  benefits_preference: BenefitPreferenceType;
  provider_npi: string | null;
  rendering_provider_npi: string | null;
};

export type BenefitPreferencesResponse = {
  preferences: BenefitPreference[];
};

export type BenefitPreferenceRequest = {
  drug_name: string;
  drug_form: string | null;
  drug_ndc: string | null;
  hcpcs: string;
  cpt: string | null;
  site_of_care_id: string | null;
  benefits_preference: BenefitPreferenceType;
  provider_npi: string | null;
  rendering_provider_npi: string | null;
};

export type MedicalBenefitDrugsResponse = {
  drugs: string[];
};
